import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { animated, config, to, useSpring, useTransition } from '@react-spring/web';
import WebApp from '@twa-dev/sdk';
import { useDrag } from '@use-gesture/react';
import clsx from 'clsx';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';

import { ROUTE_PATHS } from '@/shared/config/router/types';
import {
  blockBodyWhileDragging,
  unblockBodyWhileDragging,
} from '@/shared/helpers/body-scroll';
import {
  getCloudStorageValue,
  setCloudStorageValue,
} from '@/shared/helpers/cloud-storage';
import { getIsSlowDevice } from '@/shared/helpers/get-is-slow-device';
import { usePrevious } from '@/shared/hooks/use-previous';
import { useViewportHeight } from '@/shared/hooks/use-viewport-height';

import { StickyBannerPlacement } from '../model';
import {
  useStickyBannerActions,
  useStickyBannerState,
} from '../model/sticky-banner.store';

import s from './StickyBanner.module.scss';

interface Props {
  id: string;
}

const TRANSITION_PROPS_BY_PLACEMENT = {
  [StickyBannerPlacement.left]: {
    initial: {
      x: '-100%',
    },
    from: {
      x: '-100%',
    },
    enter: {
      x: '0%',
    },
    leave: {
      x: '-100%',
    },
  },
  [StickyBannerPlacement.right]: {
    initial: {
      x: '100%',
    },
    from: {
      x: '100%',
    },
    enter: {
      x: '0%',
    },
    leave: {
      x: '100%',
    },
  },
};

const getBannerPositionKey = (id: string) => {
  return `${id}-sticky-banner-position`;
};

export const StickyBanner = memo<Props>(({ id }) => {
  const { stableHeight } = useViewportHeight();

  const {
    isCollapsed,
    isOpen: isOpenProp,
    props: banner,
    offset = 0,
  } = useStickyBannerState(id) ?? {};

  const [isDragging, setIsDragging] = useState(false);
  const [positionState, setPosition] = useState<number | null>(null);

  const position = positionState ?? offset;

  const defaultBannerPosition = stableHeight / 2 - 50;

  const isSlowDevice = getIsSlowDevice();

  const bannerRef = useRef<HTMLDivElement>(null);

  const [{ top, scale, collapseOffset }, api] = useSpring(
    () => ({
      top: position,
      scale: 1,
      collapseOffset: isCollapsed ? 1 : 0,
      immediate: isSlowDevice,
    }),
    [position, isSlowDevice, isCollapsed]
  );

  const { toggleCollapse } = useStickyBannerActions();

  const prevScreenHeight = usePrevious(stableHeight);

  // Try to get stored position for banner
  useLayoutEffect(() => {
    if (!isOpenProp) return;

    void (async () => {
      const storedPositon = await getCloudStorageValue(getBannerPositionKey(id));

      const newPosition = Math.min(
        Math.max(
          0,
          storedPositon
            ? +storedPositon
            : (banner?.defaultPosition ?? defaultBannerPosition)
        ),
        stableHeight - 50
      );

      api.set({ top: newPosition + offset });

      setPosition(newPosition);

      if (prevScreenHeight !== stableHeight) {
        void setCloudStorageValue(
          getBannerPositionKey(id),
          Math.floor(newPosition).toString()
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isOpenProp, stableHeight, offset]);

  const toggleBannerVisibility = () => {
    toggleCollapse(id);
  };

  const topSafeArea =
    (WebApp.safeAreaInset?.top ?? 0) + (WebApp.contentSafeAreaInset?.top ?? 0);
  const bottomSafeArea =
    (WebApp.safeAreaInset?.bottom ?? 0) + (WebApp.contentSafeAreaInset?.bottom ?? 0);

  useDrag(
    ({ active, movement: [, my], first }) => {
      if (first) {
        setIsDragging(true);
        blockBodyWhileDragging();

        WebApp.HapticFeedback.impactOccurred('rigid');

        void api.start({
          scale: 1.1,
        });
      }

      if (active) {
        const newPosition = position + my;

        if (Math.abs(top.get() - newPosition) >= 1) {
          WebApp.HapticFeedback.selectionChanged();
        }

        void api.start({
          top: newPosition,
          immediate: true,
        });
      } else {
        void api.start({
          top: position + my,
          scale: 1,
        });

        setPosition(position + my);

        void setCloudStorageValue(
          getBannerPositionKey(id),
          Math.floor(position + my).toString()
        );

        setIsDragging(false);

        unblockBodyWhileDragging();
      }
    },
    {
      target: bannerRef.current ?? undefined,
      axis: 'y',
      from: () => [0, 0],
      bounds: () => {
        return {
          top: top.get() * -1 + topSafeArea,
          bottom: stableHeight - top.get() - bottomSafeArea - 50,
        };
      },
      preventScroll: true,
      delay: true,
    }
  );

  const shouldShowBanner = !!isOpenProp && positionState !== null;

  const [transitions, transitionApi] = useTransition<
    typeof banner,
    (typeof TRANSITION_PROPS_BY_PLACEMENT)[StickyBannerPlacement]
  >(
    shouldShowBanner ? banner : undefined,
    () => ({
      ...TRANSITION_PROPS_BY_PLACEMENT[banner?.placement ?? StickyBannerPlacement.right],
      keys: shouldShowBanner ? id : null,
      config: () => (isOpenProp ? config.gentle : config.default),
      immediate: isSlowDevice,
    }),
    [shouldShowBanner, isSlowDevice]
  );

  useEffect(() => {
    void transitionApi.start();
  }, [shouldShowBanner]);

  const { pathname } = useLocation();
  if (pathname === ROUTE_PATHS.DAYS_STREAK) return null;

  return transitions((style, currentBanner) => {
    if (!currentBanner) return null;

    const {
      content,
      placement = StickyBannerPlacement.right,
      background,
      onClick,
    } = currentBanner ?? {};

    return createPortal(
      <animated.div
        ref={bannerRef}
        className={clsx(
          s.container,
          s[placement ?? StickyBannerPlacement.right],
          isDragging && s.dragging
        )}
        style={{
          ...style,
          background,
          top,
          scale,
          x: to(
            [
              style.x,
              collapseOffset.to({
                range: [0, 1],
                output:
                  placement === StickyBannerPlacement.right
                    ? ['calc(0% - 0px)', 'calc(100% - 40px)']
                    : ['calc((0% * -1) + 0px)', 'calc((100% * -1) + 40px)'],
              }),
            ],
            (a, b) => `calc(${a} + ${b})`
          ),
        }}
        onClick={
          isDragging
            ? undefined
            : () => {
                WebApp.HapticFeedback.impactOccurred('medium');

                if (onClick) {
                  onClick();
                  return;
                }

                toggleBannerVisibility();
              }
        }
      >
        <div className={s.content}>{content}</div>
      </animated.div>,
      document.body
    );
  });
});
